import { useMatches } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { PropsWithChildren, useEffect, useState } from "react";

import { Modal } from "@/components/elements";
import { IconX } from "@/components/icons";
import { Walkthrough } from "@/components/widgets/Walkthrough/Walkthrough";
import { useProfile } from "@/services/profile/get-profile";
import { useUpdateWalkthroughCompleted } from "@/services/profile/update-walkthrough-completed";

export const WalkthroughProvider = ({ children }: PropsWithChildren) => {
  const { data: profile } = useProfile();
  const [opened, { open, close }] = useDisclosure(false);
  const walkthroughCompletedMutation = useUpdateWalkthroughCompleted();

  const [activeStep, setActiveStep] = useState(0);
  const isMobile = useMatches({ base: true, md: false });

  const [walkthroughDismissed, setWalkthroughDismissed] = useState(false);

  useEffect(() => {
    if (!profile) return;
    if (walkthroughDismissed) return;
    if (profile.additional_info.walkthrough_completed === true) return;

    open();
  }, [profile, walkthroughDismissed, open, close, walkthroughCompletedMutation]);

  const handleClose = () => {
    setWalkthroughDismissed(true);

    walkthroughCompletedMutation.mutate({
      walkthrough_completed: true,
    });

    close();
  };

  const isCompact = activeStep < 1;

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        title={null}
        withCloseButton
        closeButtonProps={{ icon: <IconX color="#FFFFFF" /> }}
        overlayProps={{ color: "#000000", opacity: 0.35, blur: 4 }}
        styles={{
          header: {
            background: "#222222",
            paddingTop: isCompact ? 14 : 18,
            paddingBottom: 0,
            paddingInline: isCompact ? 22 : 18,
            minHeight: isCompact ? 44 : undefined,
          },
          title: {
            display: "none",
          },
          close: {
            color: "#FFFFFF",
            backgroundColor: "inherit",
            marginTop: isCompact ? 4 : 0,
            marginRight: isCompact ? 4 : 0,
          },
          body: {
            background: "#222222",
            padding: 0,
          },
          content: {
            background: "#222222",
            borderRadius: isMobile ? 0 : 16,
          },
        }}
        size={isCompact ? 620 : "xl"}
        centered
      >
        <Walkthrough onDone={handleClose} onStepChange={setActiveStep} compact={isCompact} />
      </Modal>

      {children}
    </>
  );
};
